import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchOrdersByOrganizationAndBranch,
  fetchOrdersSummaryByOrganizationAndBranch,
} from "../../api/orders";
import { storage } from "../../helpers/storage";

const initialState = {
  value: {
    orders: [],
    offlineOrders: [],
    lastPage: 1,
    currentPage: 1,
    limit: process.env.REACT_APP_PAGE_LIMIT || 30,
    loading: false,
    loadingNextPage: false,
    loadingBackground: false,
    loadBackgroundFailed: false,
    ordersSummary: {
      total_orders: 0,
      total_revenue: 0.0,
    },
    lastParams: {
      organization_id: null,
      branch_id: null,
      filter_type: "",
      start_date: "",
      end_date: "",
      keyword: "",
      type: "",
    },
    error: null,
  },
};

export const loadOrders = createAsyncThunk(
  "orders/loadOrders",
  async (
    {
      organization_id,
      branch_id,
      filter_type,
      start_date,
      end_date,
      keyword,
      type,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState().orderReducer;

      const limit = state.value.limit;

      const response = await fetchOrdersByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type,
        "",
        start_date,
        end_date,
        keyword,
        1,
        limit,
        type
      );

      return {
        orders: response.data?.orders?.data || [],
        lastPage: response.data?.lastPage || 1,
        newParams: {
          organization_id: organization_id,
          branch_id: branch_id,
          filter_type: filter_type,
          start_date: start_date,
          end_date: end_date,
          keyword: keyword,
          type: type,
        },
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders.");
    }
  }
);

export const reloadOrdersInBackground = createAsyncThunk(
  "orders/reloadOrdersInBackground",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().saleReducer;

      const {
        lastParams: {
          organization_id,
          branch_id,
          filter_type,
          start_date,
          end_date,
          keyword,
          type,
        },
        currentPage,
        limit,
        lastPage,
      } = state.value;

      const totalLimit = currentPage > 3 ? limit * 3 : currentPage * limit;
      const finalCurrentPage = currentPage > 3 ? 3 : currentPage;

      const response = await fetchOrdersByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type,
        "",
        start_date,
        end_date,
        keyword,
        1,
        totalLimit,
        type
      );

      return {
        orders: response.data?.orders?.data || [],
        lastPage: lastPage,
        currentPage: finalCurrentPage,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background orders."
      );
    }
  }
);

export const reloadOrdersSummaryInBackground = createAsyncThunk(
  "sales/reloadOrdersSummaryInBackground",
  async (
    { organization_id, branch_id, filter_type, start_date, end_date, type },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetchOrdersSummaryByOrganizationAndBranch(
        organization_id,
        branch_id,
        filter_type || "last_30_days",
        "",
        start_date,
        end_date,
        "",
        type
      );

      return {
        total_orders: response.data?.total_sales || 0,
        total_revenue: response.data?.total_revenue || 0.0,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch background orders summary."
      );
    }
  }
);

export const loadOrdersIfNeeded = createAsyncThunk(
  "orders/loadOrdersIfNeeded",
  async (
    {
      organization_id,
      branch_id,
      filter_type,
      start_date,
      end_date,
      keyword,
      type,
    },
    { getState, dispatch }
  ) => {
    const state = getState().orderReducer;

    const currentParams = {
      organization_id,
      branch_id,
      filter_type,
      start_date,
      end_date,
      keyword,
      type,
    };
    const previousParams = {
      organization_id: state.value.lastParams?.organization_id,
      branch_id: state.value.lastParams?.branch_id,
      filter_type: state.value.lastParams?.filter_type,
      start_date: state.value.lastParams?.start_date,
      end_date: state.value.lastParams?.end_date,
      keyword: state.value.lastParams?.keyword,
      type: state.value.lastParams?.type,
    };

    let storageOrdersSummary = await storage.get("ordersSummary");
    storageOrdersSummary = storageOrdersSummary
      ? JSON.parse(storageOrdersSummary)
      : null;

    if (storageOrdersSummary) {
      dispatch(
        updateOrdersSummary({
          total_orders: storageOrdersSummary?.total_orders || 0,
          total_revenue: storageOrdersSummary?.total_revenue || 0.0,
        })
      );
    }

    let storageOrders = await storage.get("orders");
    storageOrders = storageOrders ? JSON.parse(storageOrders) : null;

    let storageOfflineOrders = await storage.get("offlineOrders");
    storageOfflineOrders = storageOfflineOrders
      ? JSON.parse(storageOfflineOrders)
      : null;

    if (storageOrders && storageOrders?.length) {
      if (storageOfflineOrders && storageOfflineOrders?.length) {
        dispatch(updateOfflineOrders({ orders: storageOfflineOrders }));
      }
      dispatch(updateOrders({ orders: storageOrders }));
      dispatch(updateOrderLoading(false));
    } else if (
      JSON.stringify(currentParams) !== JSON.stringify(previousParams)
    ) {
      dispatch(updateOrderLoading(true));
    }

    if (
      previousParams.branch_id &&
      previousParams.branch_id !== currentParams.branch_id
    ) {
      dispatch(updateOrderLoading(true));
    }

    dispatch(
      reloadOrdersSummaryInBackground({
        organization_id,
        branch_id,
        filter_type,
        start_date,
        end_date,
        type,
      })
    );
    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return dispatch(reloadOrdersInBackground());
    }

    return dispatch(
      loadOrders({
        organization_id,
        branch_id,
        filter_type,
        start_date,
        end_date,
        keyword,
        type,
      })
    );
  }
);

export const loadNextOrders = createAsyncThunk(
  "orders/loadNextOrders",
  async ({ page }, { getState, rejectWithValue }) => {
    try {
      const state = getState().orderReducer;

      const limit = state.value.limit;
      const keyword = state.value.lastParams?.keyword;
      const organization_id = state.value.lastParams?.organization_id;
      const branch_id = state.value.lastParams?.branch_id;
      const filter_type = state.value.lastParams?.filter_type;
      const start_date = state.value.lastParams?.start_date;
      const end_date = state.value.lastParams?.end_date;
      const type = state.value.lastParams?.type;

      if (state.value.currentPage === page) {
        return {
          orders: [],
          lastPage: state.value.lastPage,
          currentPage: page,
        };
      } else {
        const response = await fetchOrdersByOrganizationAndBranch(
          organization_id,
          branch_id,
          filter_type,
          "",
          start_date,
          end_date,
          keyword,
          page,
          limit,
          type
        );

        return {
          orders: response.data?.orders?.data || [],
          lastPage: response.data?.lastPage,
          currentPage: page,
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders.");
    }
  }
);

const order = createSlice({
  name: "orders",
  initialState,
  reducers: {
    updateOrders: (state, data) => {
      state.value.orders = data.payload.orders;
    },
    updateOfflineOrders: (state, data) => {
      state.value.offlineOrders = data.payload.orders;
    },
    updateOrdersSummary: (state, data) => {
      state.value.ordersSummary = {
        total_orders: data.payload.total_orders,
        total_revenue: data.payload.total_revenue,
      };
    },
    addNewOrder: (state, data) => {
      const { order } = data.payload;

      state.value.orders?.unshift(order);
      storage.set("orders", JSON.stringify(state.value.orders));
    },
    addNewOfflineOrder: (state, data) => {
      const { order } = data.payload;
      state.value.orders?.unshift(order);
      storage.set("orders", JSON.stringify(state.value.orders));

      state.value.offlineOrders?.unshift(order);
      storage.set("offlineOrders", JSON.stringify(state.value.offlineOrders));
    },
    removeOfflineOrder: (state, data) => {
      const { order } = data.payload;

      const orderIndex = state.value.orders?.findIndex(
        (el) => JSON.stringify(el) === JSON.stringify(order)
      );
      const offlineOrderIndex = state.value.offlineOrders?.findIndex(
        (el) => JSON.stringify(el) === JSON.stringify(order)
      );

      if (orderIndex !== -1) {
        state.value.orders?.splice(orderIndex, 1);
      }

      if (offlineOrderIndex !== -1) {
        state.value.offlineOrders?.splice(offlineOrderIndex, 1);
      }

      storage.set("orders", JSON.stringify(state.value.orders));
      storage.set("offlineOrders", JSON.stringify(state.value.offlineOrders));
    },
    updateExistingOrder: (state, data) => {
      const { order } = data.payload;
      const exists = state.value.orders?.findIndex(
        (el) => el?.id === order?.id
      );

      if (exists !== -1) {
        state.value.orders[exists] = order;
      }
      storage.set("orders", JSON.stringify(state.value.orders));
    },
    updateOrderLoading: (state, data) => {
      state.value.loading = data.payload;
      state.value.error = null;
    },
    deleteExistingOrder: (state, data) => {
      const { order } = data.payload;
      const exists = state.value.orders?.findIndex(
        (el) => el?.id === order?.id
      );

      if (exists !== -1) {
        state.value.orders?.splice(exists, 1);
      }

      storage.set("orders", JSON.stringify(state.value.orders));
    },
    clearOrders: () => {
      return {
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reloadOrdersSummaryInBackground.pending, (state) => {})
      .addCase(reloadOrdersSummaryInBackground.fulfilled, (state, action) => {
        state.value.ordersSummary = {
          total_orders: action.payload.total_orders,
          total_revenue: action.payload.total_revenue,
        };

        storage.set("ordersSummary", JSON.stringify(state.value.ordersSummary));
      })
      .addCase(reloadOrdersSummaryInBackground.rejected, (state, action) => {});

    builder
      .addCase(loadOrders.pending, (state) => {})
      .addCase(loadOrders.fulfilled, (state, action) => {
        const previousOfflineOrders =
          state.value.offlineOrders?.filter(
            (order) => order?.branch_id === action.payload.newParams?.branch_id
          ) || [];
        const newOrders = [...previousOfflineOrders, ...action.payload.orders];

        state.value.loading = false;
        state.value.orders = newOrders;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = 1;
        state.value.lastParams = action.payload.newParams;

        storage.set("orders", JSON.stringify(newOrders));
      })
      .addCase(loadOrders.rejected, (state, action) => {
        state.value.loading = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(loadNextOrders.pending, (state) => {
        state.value.loadingNextPage = true;
        state.value.error = null;
      })
      .addCase(loadNextOrders.fulfilled, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.orders = [...state.value.orders, ...action.payload.orders];
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set(
          "orders",
          JSON.stringify([...state.value.orders, ...action.payload.orders])
        );
      })
      .addCase(loadNextOrders.rejected, (state, action) => {
        state.value.loadingNextPage = false;
        state.value.error = action.payload;
        state.value.lastPage = state.value.currentPage;
      });

    builder
      .addCase(reloadOrdersInBackground.pending, (state) => {
        state.value.loadingBackground = true;
        state.value.loadBackgroundFailed = false;
      })
      .addCase(reloadOrdersInBackground.fulfilled, (state, action) => {
        const previousOfflineOrders =
          state.value.offlineOrders?.filter(
            (order) => order?.branch_id === state.value.lastParams.branch_id
          ) || [];
        const newOrders = [...previousOfflineOrders, ...action.payload.orders];

        state.value.loadingBackground = false;
        state.value.orders = newOrders;
        state.value.lastPage = action.payload.lastPage;
        state.value.currentPage = action.payload.currentPage;

        storage.set("orders", JSON.stringify(newOrders));
      })
      .addCase(reloadOrdersInBackground.rejected, (state, action) => {
        state.value.loadingBackground = false;
        state.value.error = action.payload;
        state.value.loadBackgroundFailed = true;
        state.value.lastPage = state.value.currentPage;
      });
  },
});

export const {
  updateOrders,
  addNewOrder,
  updateOrdersSummary,
  updateExistingOrder,
  updateOrderLoading,
  deleteExistingOrder,
  addNewOfflineOrder,
  removeOfflineOrder,
  updateOfflineOrders,
  clearOrders,
} = order.actions;

export default order.reducer;
